import { useEffect, useMemo, useState } from "react";
import { JobCardData } from "../../types/AllocationTypes";
import { unallocatedCases } from "../../types/ReadyCasesTypes";
import ToothNumberPicker from "../admin/ToothNumberPicker";
import { AutoGrowField, RedesignScopeTag } from "../admin/unitsTableShared";
import {
  BILLING_CATEGORIES,
  flattenToothNumbers,
  formatTreatments,
  isJuCase,
  MODEL_CATEGORY_OPTIONS,
  OTHER_ITEM_OPTIONS,
  ModelType,
  hasModelCategorySelection,
  modelDesignerUnits,
  parseModelTypes,
  parseOtherItemTypes,
  persistModelTypes,
  resolveBillingCategory,
  serializeModelTypes,
  suggestBillingCategory,
} from "../../utils/jobCard";
import {
  singleEmployeeName,
  splitEmployeeNames,
} from "../../utils/employeeNames";
import {
  caseChatHrefFromAllocationId,
  isLiveAllocationCaseId,
} from "../../utils/caseIds";

type QC = {
  id: number;
  name: string;
};

type JobCardMode = "designed" | "qc2";

export type JobCardSubmitOptions = {
  client_comments_read?: boolean;
};

interface JobCardModalProps {
  show: boolean;
  mode: JobCardMode;
  row: unallocatedCases | null;
  queueIndex?: number;
  queueTotal?: number;
  onClose: () => void;
  onSubmit: (jobCard: JobCardData, options?: JobCardSubmitOptions) => void;
  submitting?: boolean;
  employee?: any;
  user?: any;
  empList?: any[];
  filesByList?: any[];
  passkey: string;
  setPasskey: (val: string) => void;
  customQC: QC | null;
  setCustomQC: (val: QC | null) => void;
  actionNote: string;
  setActionNote: (val: string) => void;
  error?: string;
}

const emptyForm = {
  tooth_numbers: "",
  units: "",
  type: "",
  notes: "",
  model: "" as "" | "Yes" | "No",
  model_types: [] as Array<"" | ModelType>,
  model_notes: "",
  files_by: "",
  model_by: "",
  pc_number: "",
};

const QC2_FINAL_CHECKS = [
  {
    id: "pictures",
    label: "Pictures checked",
    hints: ["Occlusal", "Grid", "Proximal", "Notes pics"],
  },
  {
    id: "html",
    label: "HTML checked",
    hints: ["View", "Pre op", "Correct STL names"],
  },
  {
    id: "stl",
    label: "STL count done",
    hints: ["Die", "Crown", "Model"],
  },
  {
    id: "clientNotes",
    label: "Notes for client mentioned",
    hints: [] as string[],
  },
] as const;

type Qc2FinalCheckId = (typeof QC2_FINAL_CHECKS)[number]["id"];

const emptyQc2Checks: Record<Qc2FinalCheckId, boolean> = {
  pictures: false,
  html: false,
  stl: false,
  clientNotes: false,
};

const JobCardModal = ({
  show,
  mode,
  row,
  queueIndex = 0,
  queueTotal = 1,
  onClose,
  onSubmit,
  submitting = false,
  employee,
  user,
  empList,
  filesByList,
  passkey,
  setPasskey,
  customQC,
  setCustomQC,
  actionNote,
  setActionNote,
  error,
}: JobCardModalProps) => {
  const [form, setForm] = useState(emptyForm);
  const [categoryEdited, setCategoryEdited] = useState(false);
  const [qc2Checks, setQc2Checks] = useState(emptyQc2Checks);
  const [clientCommentsRead, setClientCommentsRead] = useState(false);

  const treatments = formatTreatments(row?.service_type);
  const typeOptions = useMemo(() => [...BILLING_CATEGORIES], []);
  const requiresClientCommentsAck =
    Boolean(row) &&
    isLiveAllocationCaseId(row?.case_id) &&
    Number(row?.comment_count) > 0;

  const autoBillingCategory = (
    unitsValue: string,
    modelValue: "" | "Yes" | "No",
  ) =>
    suggestBillingCategory({
      serviceType: row?.service_type,
      units: Number(unitsValue) || 0,
      model: modelValue === "Yes",
    });

  useEffect(() => {
    if (!show || !row) return;

    const saved = row.job_card;
    const juCase = isJuCase(row.case_id);
    const savedTypes = parseModelTypes(saved);
    const modelValue: "" | "Yes" | "No" =
      saved == null
        ? juCase
          ? "Yes"
          : ""
        : hasModelCategorySelection(savedTypes)
          ? "Yes"
          : "No";
    const unitsValue = String(
      saved?.units ??
        (row.design_unit != null && Number(row.design_unit) > 0
          ? row.design_unit
          : row.delivery_units || ""),
    );
    const suggested = autoBillingCategory(unitsValue, modelValue);
    const resolved = resolveBillingCategory(saved?.type, {
      serviceType: row.service_type,
      units: Number(unitsValue) || 0,
      model: modelValue === "Yes",
    });
    const orderFormTeeth = flattenToothNumbers(row.tooth_numbers);
    const workedTeeth = saved?.tooth_numbers || orderFormTeeth;

    setCategoryEdited(Boolean(saved?.type) && resolved !== suggested);
    const primaryDesigner = singleEmployeeName(row.allocated_Designer);
    const modelTypesValue: Array<"" | ModelType> =
      modelValue === "Yes"
        ? hasModelCategorySelection(savedTypes)
          ? savedTypes
          : juCase
            ? persistModelTypes(true, [...savedTypes, "normal"])
            : savedTypes
        : parseOtherItemTypes(savedTypes);
    setForm({
      tooth_numbers: workedTeeth,
      units: unitsValue,
      type: resolved,
      notes: saved?.notes || "",
      model: modelValue,
      model_types: modelTypesValue,
      model_notes: saved?.model_notes || "",
      files_by: singleEmployeeName(saved?.files_by) || primaryDesigner,
      model_by:
        singleEmployeeName(saved?.model_by) ||
        (modelValue === "Yes" ? primaryDesigner : ""),
      pc_number: String(saved?.pc_number ?? "").trim(),
    });
    setQc2Checks(emptyQc2Checks);
    setClientCommentsRead(false);
  }, [show, row]);

  useEffect(() => {
    if (!customQC) return;
    const inList = (empList || []).some(
      (item: any) => Number(item.emp_id) === Number(customQC.id),
    );
    if (!inList) setCustomQC(null);
  }, [customQC, empList, setCustomQC]);

  if (!show || !row) return null;

  const orderFormTeeth = flattenToothNumbers(row.tooth_numbers);
  const isAdmin = user?.role === "Super Admin";
  const showPasskey = !employee && !isAdmin;
  const units = Number(form.units);
  const modelYes = form.model === "Yes";
  const selectedTypes = parseModelTypes(form.model_types);
  const qc2ChecksIncomplete =
    mode === "qc2" && Object.values(qc2Checks).some((checked) => !checked);
  const clientCommentsAckIncomplete =
    requiresClientCommentsAck && !clientCommentsRead;
  const isInvalid =
    !form.type.trim() ||
    !Number.isFinite(units) ||
    units <= 0 ||
    !form.model ||
    (modelYes && !hasModelCategorySelection(selectedTypes)) ||
    (showPasskey && !passkey) ||
    qc2ChecksIncomplete ||
    clientCommentsAckIncomplete ||
    submitting;

  const handleSubmit = () => {
    if (isInvalid) return;
    const allocatedNames = splitEmployeeNames(row.allocated_Designer);
    const allocatedPrimary = allocatedNames[0] || "";
    // Designed credit: admin/super-admin selected name, else clicker, else allocated.
    const designedCreditName =
      mode === "designed"
        ? singleEmployeeName(customQC?.name) ||
          singleEmployeeName(employee?.emp_name) ||
          (isAdmin ? singleEmployeeName(user?.user_name) : "") ||
          allocatedPrimary
        : allocatedPrimary;
    const designerNames =
      mode === "designed" && designedCreditName
        ? [designedCreditName]
        : allocatedNames;
    const primaryDesigner = designerNames[0] || allocatedPrimary;
    const existingUnits = row.job_card?.designer_units || {};
    const designer_units: Record<string, number> = {};

    if (designerNames.length <= 1) {
      if (primaryDesigner) designer_units[primaryDesigner] = units;
    } else {
      let knownSum = 0;
      for (const name of designerNames) {
        const value = Number(existingUnits[name]) || 0;
        designer_units[name] = value;
        knownSum += value;
      }
      if (knownSum <= 0) {
        designer_units[primaryDesigner] = units;
        for (let i = 1; i < designerNames.length; i++) {
          designer_units[designerNames[i]] = 0;
        }
      } else if (knownSum !== units) {
        // Keep relative split across designers when total units change
        for (const name of designerNames) {
          designer_units[name] = Math.round(
            (designer_units[name] / knownSum) * units,
          );
        }
        const roundedSum = Object.values(designer_units).reduce(
          (sum, value) => sum + value,
          0,
        );
        designer_units[primaryDesigner] += units - roundedSum;
      }
    }

    onSubmit(
      {
        tooth_numbers: form.tooth_numbers.trim(),
        units,
        type: form.type.trim(),
        notes: form.notes.trim(),
        units_notes: String(row.job_card?.units_notes ?? "").trim(),
        model: modelYes,
        model_type: serializeModelTypes(
          persistModelTypes(modelYes, selectedTypes),
        ),
        model_types: persistModelTypes(modelYes, selectedTypes),
        model_notes: modelYes ? form.model_notes.trim() : "",
        files_by:
          singleEmployeeName(form.files_by) ||
          designedCreditName ||
          primaryDesigner,
        model_by: modelYes
          ? singleEmployeeName(form.model_by) ||
            designedCreditName ||
            primaryDesigner
          : "",
        delivery_tooth_numbers: form.tooth_numbers.trim(),
        designer_units,
        files_units: Number(row.job_card?.files_units) || 0,
        model_units: modelDesignerUnits(modelYes, selectedTypes),
        pc_number: form.pc_number.trim(),
      },
      requiresClientCommentsAck
        ? { client_comments_read: true }
        : undefined,
    );
  };

  const fieldClass =
    "w-full border border-[#2fa9bf] bg-white px-3 py-2 rounded-lg text-sm text-[#4A4A48] outline-none focus:border-[#2296aa] focus:ring-2 focus:ring-[#2fa9bf]/20";
  const unusedFieldClass = `${fieldClass} !bg-[#F8F8F6] text-[#8A8A85] cursor-not-allowed`;
  const labelClass = "block text-sm font-medium mb-1 text-[#4A4A48]";
  const unusedLabelClass = `${labelClass} text-[#8A8A85]`;
  const RequiredMark = () => (
    <span className="ml-0.5 text-red-500" aria-hidden="true">
      *
    </span>
  );
  const modelFieldsDisabled = !modelYes;
  const canSelectQc =
    employee?.role === "Admin" ||
    employee?.role === "Executive" ||
    employee?.role === "Super Admin" ||
    user?.role === "Super Admin";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3 sm:p-4">
      <div className="flex w-full max-w-6xl max-h-[92vh] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-[#E8E8E4] bg-[#2fa9bf] px-5 py-4 text-white sm:px-6">
          <div className="min-w-0">
            <h3 className="text-lg font-bold sm:text-xl">
              {mode === "qc2" ? "Review Job Card" : "Job Card"}
            </h3>
            <p className="mt-0.5 flex flex-wrap items-center gap-2 text-sm text-white/90">
              <span>
                Case {row.case_id}
                {queueTotal > 1 ? ` (${queueIndex + 1} of ${queueTotal})` : ""}
              </span>
              <RedesignScopeTag labels={row.redesign_category} />
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-2 text-xl leading-none text-white/80 hover:bg-white/15 hover:text-white"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 sm:px-6">
          {requiresClientCommentsAck ? (
            <section className="mb-4 rounded-2xl border border-amber-300 bg-amber-50 p-4 sm:p-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-amber-900">
                    This case has additional client comments
                  </p>
                  <p className="mt-1 text-sm text-amber-800">
                    Please read the client comments before continuing. System
                    uploads are not included.
                  </p>
                </div>
                <a
                  href={caseChatHrefFromAllocationId(row.case_id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex shrink-0 items-center justify-center rounded-xl bg-amber-600 px-3 py-2 text-sm font-medium text-white hover:bg-amber-700"
                >
                  Open comments
                </a>
              </div>
              <label className="mt-3 flex cursor-pointer items-start gap-2">
                <input
                  type="checkbox"
                  checked={clientCommentsRead}
                  onChange={() => setClientCommentsRead((prev) => !prev)}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-[#2fa9bf]"
                />
                <span className="text-sm font-semibold text-[#4A4A48]">
                  I have read the client comments
                  <RequiredMark />
                </span>
              </label>
            </section>
          ) : null}

          <section className="rounded-2xl border border-[#7ECAD8] bg-[#E7F6F8] p-4 sm:p-5">
            <div className="mb-3 flex flex-wrap items-baseline gap-x-2 text-sm text-[#4A4A48]">
              <span className="font-medium">Order form tooth numbers:</span>
              <span className="font-semibold text-[#2a7d8c]">
                {orderFormTeeth || "—"}
              </span>
            </div>

            <div className="mb-4">
              <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
                <label className={labelClass}>Tooth numbers worked on</label>
                <span className="text-[11px] text-[#8A8A85]">
                  — auto-filled from client&apos;s order form
                </span>
              </div>
              <ToothNumberPicker
                value={form.tooth_numbers}
                showEditLabel
                onChange={(value) =>
                  setForm((prev) => ({
                    ...prev,
                    tooth_numbers: value,
                  }))
                }
              />
            </div>

            <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-3">
              <div className="min-w-0">
                <label className={labelClass}>
                  Units
                  <RequiredMark />
                </label>
                <input
                  type="number"
                  min={1}
                  value={form.units}
                  onChange={(e) => {
                    const units = e.target.value;
                    setForm((prev) => ({
                      ...prev,
                      units,
                      type: categoryEdited
                        ? prev.type
                        : autoBillingCategory(units, prev.model),
                    }));
                  }}
                  className={fieldClass}
                />
              </div>

              <div className="min-w-0">
                <label className={labelClass}>
                  Treatments
                  <RequiredMark />
                </label>
                <div
                  className={`${fieldClass} truncate bg-white text-[#4A4A48]`}
                >
                  {treatments || "N/A"}
                </div>
              </div>

              <div className="min-w-0">
                <label className={labelClass}>
                  Billing category
                  <RequiredMark />
                </label>
                <select
                  value={
                    (typeOptions as string[]).includes(form.type)
                      ? form.type
                      : ""
                  }
                  onChange={(e) => {
                    setCategoryEdited(true);
                    setForm((prev) => ({ ...prev, type: e.target.value }));
                  }}
                  className={fieldClass}
                >
                  <option value="">Select billing category</option>
                  {typeOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              <div className="min-w-0">
                <label className={labelClass}>
                  Model
                  <RequiredMark />
                </label>
                <select
                  value={form.model}
                  onChange={(e) => {
                    const model = e.target.value as "" | "Yes" | "No";
                    const juCase = isJuCase(row.case_id);
                    setForm((prev) => ({
                      ...prev,
                      model,
                      model_types:
                        model === "Yes"
                          ? hasModelCategorySelection(prev.model_types)
                            ? prev.model_types
                            : juCase
                              ? persistModelTypes(true, [
                                  ...parseOtherItemTypes(prev.model_types),
                                  "normal",
                                ])
                              : prev.model_types
                          : parseOtherItemTypes(prev.model_types),
                      model_notes: model === "Yes" ? prev.model_notes : "",
                      model_by:
                        model === "Yes"
                          ? prev.model_by ||
                            singleEmployeeName(row.allocated_Designer)
                          : "",
                      type: categoryEdited
                        ? prev.type
                        : autoBillingCategory(prev.units, model),
                    }));
                  }}
                  className={fieldClass}
                >
                  <option value="">Select</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>

              <div className="min-w-0">
                <label
                  className={
                    modelFieldsDisabled ? unusedLabelClass : labelClass
                  }
                >
                  Model type
                  {modelYes ? <RequiredMark /> : null}
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {MODEL_CATEGORY_OPTIONS.map((option) => {
                    const checked = selectedTypes.includes(option.value);
                    return (
                      <label
                        key={option.value}
                        className={`flex min-w-[5.5rem] items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
                          modelFieldsDisabled
                            ? "cursor-not-allowed border-[#E8E8E4] bg-[#F8F8F6] text-[#8A8A85]"
                            : checked
                              ? "cursor-pointer border-[#2fa9bf] bg-[#2fa9bf] text-white"
                              : "cursor-pointer border-[#C9D4D6] bg-white text-[#4A4A48] hover:border-[#2fa9bf]"
                        }`}
                      >
                        <input
                          type="checkbox"
                          disabled={modelFieldsDisabled}
                          checked={checked}
                          onChange={() =>
                            setForm((prev) => {
                              const current = parseModelTypes(
                                prev.model_types,
                              );
                              const next = current.includes(option.value)
                                ? current.filter(
                                    (type) => type !== option.value,
                                  )
                                : [...current, option.value];
                              return { ...prev, model_types: next };
                            })
                          }
                          className="h-4 w-4 shrink-0 accent-[#2fa9bf] disabled:cursor-not-allowed"
                        />
                        <span>{option.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="min-w-0">
                <label
                  className={
                    modelFieldsDisabled ? unusedLabelClass : labelClass
                  }
                >
                  Model by
                </label>
                <select
                  value={form.model_by}
                  disabled={modelFieldsDisabled}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      model_by: e.target.value,
                    }))
                  }
                  className={
                    modelFieldsDisabled ? unusedFieldClass : fieldClass
                  }
                >
                  <option value="">Select employee</option>
                  {(filesByList || []).map((item: any) => (
                    <option
                      key={item.emp_id || item.emp_name}
                      value={item.emp_name}
                    >
                      {item.emp_name}
                    </option>
                  ))}
                  {form.model_by &&
                  !(filesByList || []).some(
                    (item: any) => item.emp_name === form.model_by,
                  ) ? (
                    <option value={form.model_by}>{form.model_by}</option>
                  ) : null}
                </select>
              </div>

              {showPasskey ? (
                <div className="min-w-0 sm:col-span-3">
                  <label className={labelClass}>
                    Passkey
                    <RequiredMark />
                  </label>
                  <input
                    type="password"
                    placeholder="Enter passkey"
                    value={passkey}
                    onChange={(e) => setPasskey(e.target.value)}
                    className={`${fieldClass} max-w-sm`}
                  />
                </div>
              ) : null}
            </div>
          </section>

          <section className="mt-4 rounded-2xl border border-[#E5E5E0] bg-white p-4 sm:p-5">
            <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
                <div className="min-w-0">
                  <label className={labelClass}>Files by</label>
                  <select
                    value={form.files_by}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        files_by: e.target.value,
                      }))
                    }
                    className={fieldClass}
                  >
                    <option value="">Select employee</option>
                    {(filesByList || []).map((item: any) => (
                      <option
                        key={item.emp_id || item.emp_name}
                        value={item.emp_name}
                      >
                        {item.emp_name}
                      </option>
                    ))}
                    {form.files_by &&
                    !(filesByList || []).some(
                      (item: any) => item.emp_name === form.files_by,
                    ) ? (
                      <option value={form.files_by}>{form.files_by}</option>
                    ) : null}
                  </select>
                </div>

                <div className="min-w-0">
                  <label className={labelClass}>PC number</label>
                  <input
                    type="text"
                    value={form.pc_number}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        pc_number: e.target.value,
                      }))
                    }
                    className={fieldClass}
                    placeholder="Optional"
                  />
                </div>

                <div className="min-w-0">
                  <label className={labelClass}>Notes</label>
                  <AutoGrowField
                    value={form.notes}
                    onChange={(notes) =>
                      setForm((prev) => ({ ...prev, notes }))
                    }
                    className={fieldClass}
                    placeholder="Job card notes"
                  />
                </div>

                <div className="min-w-0">
                  <label
                    className={
                      modelFieldsDisabled ? unusedLabelClass : labelClass
                    }
                  >
                    Model notes
                  </label>
                  <AutoGrowField
                    value={form.model_notes}
                    disabled={modelFieldsDisabled}
                    onChange={(model_notes) =>
                      setForm((prev) => ({
                        ...prev,
                        model_notes,
                      }))
                    }
                    className={
                      modelFieldsDisabled ? unusedFieldClass : fieldClass
                    }
                    placeholder="Model notes"
                  />
                </div>

                {canSelectQc ? (
                  <div className="min-w-0">
                    <label className={labelClass}>
                      {mode === "qc2" ? "Select QC2" : "Select Designed"}
                    </label>
                    <select
                      value={customQC?.id || ""}
                      onChange={(e) => {
                        const selected = empList?.find(
                          (item: any) =>
                            item.emp_id === Number(e.target.value),
                        );
                        setCustomQC(
                          selected
                            ? {
                                id: selected.emp_id,
                                name: selected.emp_name,
                              }
                            : null,
                        );
                      }}
                      className={fieldClass}
                    >
                      <option value="">Select</option>
                      {empList?.map((item: any) => (
                        <option key={item.emp_id} value={item.emp_id}>
                          {item.emp_name}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}

                <div className="min-w-0 sm:col-span-2">
                  <label className={labelClass}>Other items</label>
                  <div className="flex flex-wrap gap-x-4 gap-y-2">
                    {OTHER_ITEM_OPTIONS.map((option) => {
                      const checked = selectedTypes.includes(option.value);
                      return (
                        <label
                          key={option.value}
                          className="flex cursor-pointer items-center gap-2 text-sm text-[#4A4A48]"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() =>
                              setForm((prev) => {
                                const current = parseModelTypes(
                                  prev.model_types,
                                );
                                const next = current.includes(option.value)
                                  ? current.filter(
                                      (type) => type !== option.value,
                                    )
                                  : [...current, option.value];
                                return { ...prev, model_types: next };
                              })
                            }
                            className="h-4 w-4 shrink-0 accent-[#2fa9bf]"
                          />
                          <span>{option.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {mode === "qc2" ? (
                  <div className="min-w-0 sm:col-span-2">
                    <label className={labelClass}>QC2 note</label>
                    <AutoGrowField
                      value={actionNote}
                      onChange={setActionNote}
                      className={fieldClass}
                      placeholder="Enter QC2 note (optional)"
                    />
                  </div>
                ) : null}
              </div>
          </section>

          {mode === "qc2" ? (
            <section className="mt-4 rounded-2xl border border-[#7ECAD8] bg-[#E7F6F8] p-4 sm:p-5">
              <div className="mb-4 flex items-center gap-3">
                <h4 className="shrink-0 text-xs font-bold uppercase tracking-[0.12em] text-[#2fa9bf]">
                  Final checks — required before confirming
                </h4>
                <div className="h-px min-w-0 flex-1 bg-[#7ECAD8]" />
              </div>
              <div className="grid grid-cols-1 gap-x-10 gap-y-4 sm:grid-cols-2">
                {QC2_FINAL_CHECKS.map((check) => (
                  <label
                    key={check.id}
                    className="flex cursor-pointer items-start gap-2"
                  >
                    <input
                      type="checkbox"
                      checked={qc2Checks[check.id]}
                      onChange={() =>
                        setQc2Checks((prev) => ({
                          ...prev,
                          [check.id]: !prev[check.id],
                        }))
                      }
                      className="mt-0.5 h-4 w-4 shrink-0 accent-[#2fa9bf]"
                    />
                    <span>
                      <span className="text-sm font-semibold text-[#4A4A48]">
                        {check.label}
                        <RequiredMark />
                      </span>
                      {check.hints.length ? (
                        <span className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[12px] text-[#2fa9bf]">
                          {check.hints.map((hint) => (
                            <span key={hint}>{hint}</span>
                          ))}
                        </span>
                      ) : null}
                    </span>
                  </label>
                ))}
              </div>
            </section>
          ) : null}

          {error ? (
            <p className="mt-3 text-sm text-red-600">{error}</p>
          ) : null}
        </div>

        <div className="flex shrink-0 justify-end gap-2 border-t border-[#E8E8E4] bg-[#FAFAF8] px-5 py-3 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl bg-[#E8E8E4] px-4 py-2 text-sm font-medium text-[#4A4A48] hover:bg-[#DDDDD8]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isInvalid}
            className="rounded-xl bg-[#2fa9bf] px-4 py-2 text-sm font-medium text-white hover:bg-[#2296aa] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting
              ? "Saving..."
              : mode === "qc2"
                ? "Confirm QC2"
                : "Save job card"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default JobCardModal;
